import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarClock, Save, Loader2 } from "lucide-react";

const TZ_OFFSET = new Date().getTimezoneOffset();
const TZ_NAME = Intl.DateTimeFormat().resolvedOptions().timeZone;

export default function SchedulePanel({ crawlId }) {
  const [cfg, setCfg] = useState({ enabled: false, time_hhmm: "02:00", mode: "extend", tz_offset_minutes: TZ_OFFSET });
  const [lastRun, setLastRun] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!crawlId) return;
    api.getSchedule(crawlId).then((s) => {
      setCfg({
        enabled: !!s.enabled,
        time_hhmm: s.time_hhmm || "02:00",
        mode: s.mode || "extend",
        tz_offset_minutes: TZ_OFFSET,
      });
      setLastRun(s.last_run_at || null);
    }).catch(() => {});
  }, [crawlId]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const s = await api.saveSchedule(crawlId, { ...cfg, tz_offset_minutes: TZ_OFFSET });
      setLastRun(s.last_run_at || null);
      toast.success(cfg.enabled ? `Scheduled daily at ${cfg.time_hhmm} (${TZ_NAME})` : "Schedule disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save schedule");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-border bg-[#0B0F18] p-5 space-y-4" data-testid="schedule-panel">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-purple-400" />
        <h3 className="font-display font-bold text-base">Nightly Schedule</h3>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-slate-600">{TZ_NAME}</span>
      </div>

      <div className="flex items-center justify-between rounded-md border border-border bg-[#0E1420] px-3 py-2.5">
        <div>
          <Label className="text-xs text-slate-300">Run automatically</Label>
          <p className="text-[10px] font-mono text-slate-600 mt-0.5">
            {lastRun ? `last run ${lastRun.slice(0, 16).replace("T", " ")} UTC` : "never run yet"}
          </p>
        </div>
        <Switch data-testid="schedule-enabled-switch" checked={cfg.enabled} onCheckedChange={(v) => set("enabled", v)} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-400">Time (your local)</Label>
          <Input data-testid="schedule-time-input" type="time" value={cfg.time_hhmm}
            onChange={(e) => set("time_hhmm", e.target.value)}
            className="bg-[#0E1420] border-border font-mono text-sm" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-400">Each night</Label>
          <Select value={cfg.mode} onValueChange={(v) => set("mode", v)}>
            <SelectTrigger data-testid="schedule-mode-select" className="bg-[#0E1420] border-border text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="extend">Extend — keep pages, add more</SelectItem>
              <SelectItem value="fresh">Fresh — re-crawl from scratch</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <p className="text-[11px] text-slate-600 leading-relaxed">
        Extend grants the job another page budget and a fresh time budget, then continues from its saved frontier —
        the dataset keeps growing and each run adds a new point to the quality score history.
      </p>

      <Button data-testid="schedule-save-button" onClick={save} disabled={busy || !crawlId}
        className="bg-purple-500 hover:bg-purple-400 text-slate-950 font-semibold">
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
        Save Schedule
      </Button>
    </div>
  );
}
